import { chunkArray } from './chunk';

// eslint-disable-next-line import/prefer-default-export
export class BatchFetcher<TRequest, TResponse> {
  constructor(private readonly resolveEndpoint: (request: TRequest) => string) {}

  post(
    endpoint: string,
    data: TRequest[],
  ): JQuery.jqXHR<TResponse[]> {
    return $.ajax({
      url: endpoint,
      type: 'POST',
      contentType: 'application/json; charset=utf-8',
      dataType: 'json',
      data: JSON.stringify(data),
    }) as JQuery.jqXHR<TResponse[]>;
  }

  async fetchOne(request: TRequest): Promise<TResponse[]> {
    const endpoint = this.resolveEndpoint(request);
    return this.post(endpoint, [request]);
  }

  async fetchChunked(
    products: TRequest[],
    onChunk: (data: TResponse[]) => void,
    onError?: (error: JQuery.jqXHR) => void,
  ): Promise<TResponse[]> {
    if (products.length === 0) return [];

    // Group by resolved endpoint
    const byEndpoint = new Map<string, TRequest[]>();
    products.forEach((product) => {
      const endpoint = this.resolveEndpoint(product);
      const group = byEndpoint.get(endpoint);
      if (group) {
        group.push(product);
      } else {
        byEndpoint.set(endpoint, [product]);
      }
    });

    const allResults: TResponse[] = [];

    const fetchGroup = async (endpoint: string, items: TRequest[]) => {
      const chunks = chunkArray(items);
      // Sequential fetching within a group to avoid overwhelming the server
      // eslint-disable-next-line no-restricted-syntax, no-await-in-loop
      for (let i = 0; i < chunks.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await this.post(endpoint, chunks[i]);
          onChunk(data);
          allResults.push(...data);
        } catch (error: any) {
          onError?.(error);
        }
      }
    };

    await Promise.all(
      Array.from(byEndpoint.entries()).map(
        ([endpoint, items]) => fetchGroup(endpoint, items),
      ),
    );

    return allResults;
  }
}
